import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Camera, CalendarPlus } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { EVENT } from "@/data/event";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return setStatus("error");
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          return setStatus("paid");
        }
        if (data.payment_status === "expired" || tries > 12) return setStatus("error");
      } catch {
        if (tries > 12) return setStatus("error");
      }
      tries += 1;
      setTimeout(poll, 2000);
    };
    poll();
  }, [params]);

  const hasPoster = order?.line_items?.some((item) => item.label?.includes("Poster Print"));

  return (
    <div className="flex min-h-screen min-w-0 items-center justify-center bg-[#090909] px-4 py-10 text-[#F5F0E8] sm:px-5">
      <div className="w-full min-w-0 max-w-lg text-center">
        {status === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="animate-spin mx-auto neon-blue" size={40} />
            <p className="font-accent tracking-widest mt-6 text-xl">CONFIRMING YOUR SPOT…</p>
          </div>
        )}
        {status === "paid" && order && (
          <div data-testid="payment-success">
            <CheckCircle2 className="mx-auto neon-green" size={56} />
            <p className="font-accent tracking-[0.3em] neon-red text-sm mt-6">FOTOS ON THE GEAUX PRESENTS</p>
            <h1 className="font-display text-5xl tracking-tighter mt-2">YOU'RE IN.</h1>
            <p className="text-[#F5F0E8]/60 mt-3">See you at POLE &amp; POLAROIDS — September 25.</p>
            <div className="mt-8 border border-white/10 rounded-xl p-6 text-left">
              <div className="flex justify-between font-accent tracking-widest text-sm text-[#F5F0E8]/50">
                <span>CONFIRMATION</span><span className="neon-green">{order.order_id}</span>
              </div>
              <div className="mt-4 space-y-2">
                {order.line_items?.map((li, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-[#F5F0E8]/80">{li.label}</span>
                    <span>${li.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-4 pt-4 border-t border-white/10 font-accent tracking-widest">
                <span>TOTAL</span><span className="neon-green">${order.total?.toFixed(2)}</span>
              </div>
            </div>
            <p className="text-[#F5F0E8]/40 text-sm mt-4">A confirmation email is on its way to your inbox.</p>
            {hasPoster && (
              <div className="mt-4 border border-[#175CFF]/30 bg-[#175CFF]/10 rounded-lg p-4 text-left">
                <p className="font-accent tracking-widest text-sm text-[#175CFF]">POSTER ADDED</p>
                <p className="text-sm text-[#F5F0E8]/70 mt-1">After the event, we'll send instructions for choosing the photo for your poster print.</p>
              </div>
            )}

            {/* QR ticket + add to calendar */}
            <div className="mt-6 grid sm:grid-cols-[auto_1fr] gap-5 items-center border border-white/10 rounded-xl p-5 text-left">
              <div className="bg-white p-2.5 rounded-lg mx-auto" data-testid="ticket-qr">
                <QRCodeSVG value={`POLE-POLAROIDS|${order.order_id}`} size={110} level="M" />
              </div>
              <div>
                <p className="font-accent tracking-widest text-xs text-[#F5F0E8]/50">YOUR QR TICKET</p>
                <p className="text-sm text-[#F5F0E8]/70 mt-1">Show this at check-in on {EVENT.dateLong.replace(", 2026", "")}. Screenshot it or keep this page handy.</p>
                <a
                  data-testid="add-to-calendar"
                  href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent("POLE & POLAROIDS")}&dates=${EVENT.calStart}/${EVENT.calEnd}&details=${encodeURIComponent("Your POLE & POLAROIDS ticket. Order " + order.order_id + ". " + EVENT.includes)}&location=${encodeURIComponent(EVENT.venueAddress)}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 mt-3 font-accent tracking-widest text-sm border border-white/25 px-5 py-2.5 rounded-full glow-hover hover:border-[#175CFF] hover:text-[#175CFF]">
                  <CalendarPlus size={15} /> ADD TO CALENDAR
                </a>
              </div>
            </div>
            <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 mt-8 font-accent tracking-widest border border-white/25 px-8 py-3 rounded-full glow-hover hover:border-[#39E75F] hover:text-[#39E75F]">
              <Camera size={16} /> BACK TO SITE
            </Link>
          </div>
        )}
        {status === "error" && (
          <div data-testid="payment-error">
            <h1 className="font-display text-4xl">We couldn't confirm that payment.</h1>
            <p className="text-[#F5F0E8]/60 mt-3">If you were charged, contact us and we'll sort it out.</p>
            <Link to="/" className="inline-block mt-8 font-accent tracking-widest border border-white/25 px-8 py-3 rounded-full">BACK TO SITE</Link>
          </div>
        )}
      </div>
    </div>
  );
}
